// frontend/src/pages/portal/PortalActivityPage.jsx
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Activity, ChevronLeft, ChevronRight, Plus, Trash2, Timer, MapPin, Flame } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

const ACTIVITY_TYPES = ['Marche', 'Course à pied', 'Vélo', 'Natation', 'Renforcement Musculaire', 'Yoga / Étirements', 'Autre']
const INTENSITIES = [
    { value: 'douce', label: 'Douce (Je peux parler)' },
    { value: 'moderee', label: 'Modérée (Je transpire un peu)' },
    { value: 'intense', label: 'Intense (Essoufflé)' }
]
const FEELINGS = [
    { emoji: '🤩', label: 'Super' },
    { emoji: '👍', label: 'Bien' },
//    { emoji: ' خ_خ', label: 'Difficile' },
    { emoji: '🥵', label: 'Difficile' },
    { emoji: '🤕', label: 'Douleurs' }
]

export default function PortalActivityPage() {
    const { token } = useParams()
    const [activities, setActivities] = useState([])
    const [loading, setLoading] = useState(true)
    const [isAdding, setIsAdding] = useState(false)
    
    const today = new Date().toISOString().split('T')[0]
    const [currentDate, setCurrentDate] = useState(today)

    const [newActivity, setNewActivity] = useState({
        activity_type: 'Marche', duration_minutes: '', distance_km: '', intensity: '', feeling: '', notes: ''
    })

    const changeDate = (days) => {
        const d = new Date(currentDate)
        d.setDate(d.getDate() + days)
        setCurrentDate(d.toISOString().split('T')[0])
    }

    const fetchActivities = async () => {
        setLoading(true)
        try {
            const res = await api.get(`/api/portal/${token}/activities?date=${currentDate}`)
            setActivities(res.data)
        } catch (e) {
            toast.error("Erreur de connexion")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchActivities() }, [currentDate])

    const handleAddActivity = async (e) => {
        e.preventDefault()
        if (!newActivity.activity_type) return toast.error("Veuillez choisir un type d'activité.")
        
        toast.loading("Enregistrement...", { id: 'saveAct' })
        try {
            const payload = { ...newActivity, date: currentDate }
            if (payload.duration_minutes === '') payload.duration_minutes = null
            if (payload.distance_km === '') payload.distance_km = null
            else payload.distance_km = parseFloat(payload.distance_km.replace(',', '.'))

            await api.post(`/api/portal/${token}/activities`, payload)
            toast.success("Activité enregistrée !", { id: 'saveAct' })
            setIsAdding(false)
            setNewActivity({ activity_type: 'Marche', duration_minutes: '', distance_km: '', intensity: '', feeling: '', notes: '' })
            fetchActivities()
        } catch (e) {
            toast.error("Erreur d'enregistrement", { id: 'saveAct' })
        }
    }

    const handleDelete = async (id) => {
        if (!confirm("Supprimer cette activité ?")) return
        try {
            await api.delete(`/api/portal/${token}/activities/${id}`)
            toast.success("Activité supprimée")
            fetchActivities()
        } catch (e) { toast.error("Erreur") }
    }

    if (loading) return <div className="flex justify-center py-20"><div className="spinner border-blue-600"/></div>

    return (
        <div className="space-y-6 animate-in fade-in pb-20">
            <div>
                <h1 className="text-2xl font-serif text-blue-800">Mouvement 🏃‍♂️</h1>
                <p className="text-gray-500 text-sm">Suivez vos efforts et célébrez vos victoires.</p>
            </div>

            <div className="flex justify-between items-center bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
                <button onClick={() => changeDate(-1)} className="p-2 hover:bg-gray-50 rounded-xl text-gray-500 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
                <div className="font-bold text-blue-700 text-sm capitalize">
                    {currentDate === today ? "Aujourd'hui" : new Date(currentDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' })}
                </div>
                <button onClick={() => changeDate(1)} disabled={currentDate === today} className={`p-2 rounded-xl transition-colors ${currentDate === today ? 'text-gray-200' : 'text-gray-500 hover:bg-gray-50'}`}><ChevronRight className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
                {activities.length === 0 && !isAdding && (
                    <div className="text-center p-8 border-2 border-dashed border-blue-200 bg-blue-50 rounded-2xl">
                        <Activity className="w-12 h-12 text-blue-300 mx-auto mb-3" />
                        <p className="text-blue-600 font-medium">Aucune activité notée pour ce jour.</p>
                    </div>
                )}
                
                {activities.map(act => (
                    <div key={act.id} className="bg-white p-4 rounded-2xl shadow-sm border border-blue-100 relative">
                        <div className="flex justify-between items-start mb-3">
                            <span className="text-sm font-bold text-blue-800 bg-blue-50 px-3 py-1 rounded-lg">
                                {act.activity_type}
                            </span>
                            <div className="flex items-center gap-3">
                                {act.feeling && <span className="text-2xl" title="Ressenti">{act.feeling}</span>}
                                <button onClick={() => handleDelete(act.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4"/></button>
                            </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-3 mt-3">
                            {act.duration_minutes && (
                                <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600 bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100">
                                    <Timer className="w-3.5 h-3.5 text-blue-500"/> {act.duration_minutes} min
                                </div>
                            )}
                            {act.distance_km && (
                                <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600 bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100">
                                    <MapPin className="w-3.5 h-3.5 text-green-500"/> {act.distance_km} km
                                </div>
                            )}
                            {act.intensity && (
                                <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600 bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100">
                                    <Flame className="w-3.5 h-3.5 text-orange-500"/> {INTENSITIES.find(i => i.value === act.intensity)?.label.split(' ')[0]}
                                </div>
                            )}
                        </div>
                        {act.notes && <p className="text-sm text-gray-600 mt-3 italic">"{act.notes}"</p>}
                    </div>
                ))}
            </div>

            {!isAdding ? (
                <button onClick={() => setIsAdding(true)} className="w-full btn bg-blue-600 text-white hover:bg-blue-700 shadow-xl shadow-blue-200/50 flex items-center justify-center gap-2 py-4 rounded-2xl">
                    <Plus className="w-5 h-5"/> Ajouter une activité
                </button>
            ) : (
                <div className="bg-white rounded-2xl p-5 shadow-xl border border-blue-200 animate-in slide-in-from-bottom-4">
                    <h3 className="font-bold text-blue-800 mb-4 flex items-center gap-2"><Activity className="w-5 h-5"/> Nouvelle session</h3>
                    <form onSubmit={handleAddActivity} className="space-y-4">
                        
                        <select required value={newActivity.activity_type} onChange={e => setNewActivity({...newActivity, activity_type: e.target.value})} className="w-full bg-gray-50 border-gray-200 focus:ring-blue-300 rounded-xl py-3 font-bold text-gray-700">
                            {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase">Durée (min)</label>
                                <div className="relative">
                                    <input type="number" placeholder="Ex: 45" value={newActivity.duration_minutes} onChange={e => setNewActivity({...newActivity, duration_minutes: e.target.value})} className="w-full bg-gray-50 border-gray-200 focus:ring-blue-300 rounded-xl pr-10" />
                                    <Timer className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase">Distance (km)</label>
                                <div className="relative">
                                    <input type="number" step="0.1" placeholder="Ex: 5.2" value={newActivity.distance_km} onChange={e => setNewActivity({...newActivity, distance_km: e.target.value})} className="w-full bg-gray-50 border-gray-200 focus:ring-blue-300 rounded-xl pr-10" />
                                    <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] font-bold text-gray-400 uppercase">Intensité</label>
                            <select value={newActivity.intensity} onChange={e => setNewActivity({...newActivity, intensity: e.target.value})} className="w-full bg-gray-50 border-gray-200 focus:ring-blue-300 rounded-xl">
                                <option value="">Non précisée</option>
                                {INTENSITIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className="text-[10px] font-bold text-gray-400 uppercase mb-2 block">Comment vous sentez-vous ?</label>
                            <div className="flex justify-between gap-2">
                                {FEELINGS.map(f => (
                                    <button 
                                        key={f.emoji} type="button"
                                        onClick={() => setNewActivity({...newActivity, feeling: f.emoji})}
                                        className={`flex-1 flex flex-col items-center p-3 rounded-xl border-2 transition-all ${newActivity.feeling === f.emoji ? 'border-blue-400 bg-blue-50' : 'border-gray-100 bg-white grayscale opacity-50 hover:grayscale-0 hover:opacity-100'}`}
                                    >
                                        <span className="text-2xl mb-1">{f.emoji}</span>
                                        <span className="text-[9px] font-bold text-gray-600 uppercase text-center leading-tight">{f.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <textarea 
                            rows="2" 
                            value={newActivity.notes}
                            onChange={e => setNewActivity({...newActivity, notes: e.target.value})}
                            placeholder="Une note ? (ex: Belle balade au soleil)"
                            className="w-full bg-gray-50 border-gray-200 focus:ring-blue-300 text-sm resize-none rounded-xl"
                        />

                        <div className="flex gap-3 pt-2">
                            <button type="button" onClick={() => setIsAdding(false)} className="flex-1 btn bg-gray-100 text-gray-600 rounded-xl py-3">Annuler</button>
                            <button type="submit" className="flex-1 btn bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3 shadow-md">Enregistrer</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    )
}